import React, { useRef, useEffect, useState } from "react";
import apiCall from "../../../common/config/apiCall";
import apiKey from "../../../common/config/apiKey";
import { FaTimes, FaSearch, FaReceipt, FaEnvelope, FaInfoCircle } from "react-icons/fa";
import { FiHash, FiMail, FiSmartphone } from "react-icons/fi";

const ManageBookingModal = ({ open, onClose, onSuccess }) => {
  const dialogRef = useRef(null);
  const [activeTab, setActiveTab] = useState("reference"); // "reference" | "email"
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [referenceData, setReferenceData] = React.useState({ bookingReference: "", emailAddress: "" });
  const [emailData, setEmailData] = React.useState({ emailAddress: "", mobileOrVehicleReg: "" });

  useEffect(() => {
    if (open && dialogRef.current) {
      dialogRef.current.showModal();
    } else if (dialogRef.current) {
      dialogRef.current.close();
    }
  }, [open]);

  const reset = () => {
    setReferenceData({ bookingReference: "", emailAddress: "" });
    setEmailData({ emailAddress: "", mobileOrVehicleReg: "" });
    setActiveTab("reference");
    setError("");
    setIsLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const validateReference = () => {
    if (!referenceData.bookingReference.trim()) { setError("Please enter your booking reference number."); return false; }
    if (!referenceData.emailAddress.trim()) { setError("Please enter your email address."); return false; }
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(referenceData.emailAddress)) { setError("Please enter a valid email address."); return false; }
    return true;
  };

  const validateEmail = () => {
    if (!emailData.emailAddress.trim()) { setError("Please enter your email address."); return false; }
    if (!emailData.mobileOrVehicleReg.trim()) { setError("Please enter your mobile number or vehicle registration."); return false; }
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(emailData.emailAddress)) { setError("Please enter a valid email address."); return false; }
    return true;
  };

  const handleSearchByReference = async () => {
    if (!validateReference()) return;
    setIsLoading(true);
    setError("");
    try {
      const payload = {
        key: apiKey,
        email: referenceData.emailAddress,
        common: "",
        reference_no: referenceData.bookingReference,
      };
      const resp = await apiCall(
        "post",
        "/login",
        payload,
        { "Content-Type": "application/json", Accept: "application/json" },
        "customer-login"
      );
      if (resp?.success && resp?.data?.token) {
        localStorage.setItem("authToken", resp.data.token);
        localStorage.setItem("customerData", JSON.stringify({
          email: referenceData.emailAddress,
          searchMethod: 'reference',
          loginTime: new Date().toISOString(),
          site: resp.data.site,
          password: resp.data.password,
        }));
        onSuccess?.();
      } else {
        setError(resp?.message || "Login failed. Please check your details and try again.");
      }
    } catch (e) {
      setError(e?.message || "Unable to connect. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchByEmail = async () => {
    if (!validateEmail()) return;
    setIsLoading(true);
    setError("");
    try {
      const payload = {
        key: apiKey,
        email: emailData.emailAddress,
        common: emailData.mobileOrVehicleReg,
        reference_no: "",
      };
      const resp = await apiCall(
        "post",
        "/login",
        payload,
        { "Content-Type": "application/json", Accept: "application/json" },
        "customer-login"
      );
      if (resp?.success && resp?.data?.token) {
        localStorage.setItem("authToken", resp.data.token);
        localStorage.setItem("customerData", JSON.stringify({
          email: emailData.emailAddress,
          searchMethod: 'email',
          loginTime: new Date().toISOString(),
          site: resp.data.site,
          password: resp.data.password,
        }));
        onSuccess?.();
      } else {
        setError(resp?.message || "Login failed. Please check your details and try again.");
      }
    } catch (e) {
      setError(e?.message || "Unable to connect. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="w-[92%] sm:w-[95%] max-w-xl bg-white rounded-3xl shadow-2xl border-0 p-0 overflow-hidden transform transition-all"
      onClose={handleClose}
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        margin: 0,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 sm:px-6 sm:py-5 bg-gradient-to-r from-primary to-[#0c9e82] text-white shadow-md">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/10 flex items-center justify-center border border-white/15">
            <FaSearch className="text-white text-xs sm:text-sm" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-bold tracking-tight">Manage My Booking(s)</h3>
            <p className="hidden xs:block text-[10px] sm:text-xs text-emerald-100/80 font-medium">Retrieve or modify your parking reservations</p>
          </div>
        </div>
        <button
          onClick={handleClose}
          className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/10 hover:bg-white/20 active:scale-95 flex items-center justify-center border border-white/10 transition-all duration-200"
          aria-label="Close modal"
        >
          <FaTimes className="text-white text-sm" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-4 bg-[#F8FAF9] max-h-[82vh] overflow-y-auto space-y-4">

        {/* Tab Controls */}
        <div className="flex p-1 bg-slate-100/80 backdrop-blur-md rounded-xl  mx-auto border border-slate-200/40">
          <button
            onClick={() => { setActiveTab("reference"); setError(""); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs sm:text-sm font-bold rounded-lg transition-all duration-200 ${activeTab === "reference"
              ? "bg-white text-slate-800 shadow-sm"
              : "text-slate-500 hover:text-slate-800"
              }`}
          >
            <FaReceipt className={activeTab === "reference" ? "text-lightprimary" : "text-slate-400"} />
            <span>Reference No</span>
          </button>
          <button
            onClick={() => { setActiveTab("email"); setError(""); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs sm:text-sm font-bold rounded-lg transition-all duration-200 ${activeTab === "email"
              ? "bg-white text-slate-800 shadow-sm"
              : "text-slate-500 hover:text-slate-800"
              }`}
          >
            <FaEnvelope className={activeTab === "email" ? "text-primary" : "text-slate-400"} />
            <span>Email Address</span>
          </button>
        </div>

        {/* Tab Content Display */}
        <div className="w-full mx-auto">
          {activeTab === "reference" ? (
            /* Reference Search Card */
            <div className="p-5 sm:p-6 rounded-2xl border border-lightprimary/15 bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="text-center mb-5">
                <div className="w-12 h-12 bg-lightprimary/10 rounded-2xl flex items-center justify-center text-lightprimary mb-2.5 mx-auto">
                  <FaReceipt className="text-lg" />
                </div>
                <h4 className="font-bold text-base sm:text-lg text-slate-800">Search by Reference No</h4>
                <p className="text-[11px] sm:text-xs text-slate-400 mt-0.5">Quickly retrieve booking via your code</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Booking Reference</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <FiHash className="text-slate-400 text-sm" />
                    </span>
                    <input
                      type="text"
                      value={referenceData.bookingReference}
                      onChange={(e) => setReferenceData({ ...referenceData, bookingReference: e.target.value })}
                      className="pl-10 w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-lightprimary/30 focus:border-lightprimary transition-all text-xs sm:text-sm placeholder-slate-300"
                      placeholder="e.g. PP-12345"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Email Address</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <FiMail className="text-slate-400 text-sm" />
                    </span>
                    <input
                      type="email"
                      value={referenceData.emailAddress}
                      onChange={(e) => setReferenceData({ ...referenceData, emailAddress: e.target.value })}
                      className="pl-10 w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-lightprimary/30 focus:border-lightprimary transition-all text-xs sm:text-sm placeholder-slate-300"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <button
                  disabled={isLoading}
                  onClick={handleSearchByReference}
                  className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-lightprimary to-[#0a856d] hover:brightness-110 active:scale-98 flex justify-center items-center gap-2 shadow-lg shadow-lightprimary/15 transition-all duration-200 text-xs sm:text-sm"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Searching Reference...</span>
                    </>
                  ) : (
                    <span>Search Booking</span>
                  )}
                </button>
              </div>
            </div>
          ) : (
            /* Email Search Card */
            <div className="p-5 sm:p-6 rounded-2xl border border-primary/15 bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="text-center mb-5">
                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-2.5 mx-auto">
                  <FaEnvelope className="text-lg" />
                </div>
                <h4 className="font-bold text-base sm:text-lg text-slate-800">Search by Email Address</h4>
                <p className="text-[11px] sm:text-xs text-slate-400 mt-0.5">Find bookings using alternate details</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Email Address</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <FiMail className="text-slate-400 text-sm" />
                    </span>
                    <input
                      type="email"
                      value={emailData.emailAddress}
                      onChange={(e) => setEmailData({ ...emailData, emailAddress: e.target.value })}
                      className="pl-10 w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all text-xs sm:text-sm placeholder-slate-300"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Mobile / Vehicle Reg</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <FiSmartphone className="text-slate-400 text-sm" />
                    </span>
                    <input
                      type="text"
                      value={emailData.mobileOrVehicleReg}
                      onChange={(e) => setEmailData({ ...emailData, mobileOrVehicleReg: e.target.value })}
                      className="pl-10 w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all text-xs sm:text-sm placeholder-slate-300"
                      placeholder="e.g. 07123456789 or AB12CDE"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <button
                  disabled={isLoading}
                  onClick={handleSearchByEmail}
                  className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-[#001f19] hover:brightness-110 active:scale-98 flex justify-center items-center gap-2 shadow-lg shadow-primary/15 transition-all duration-200 text-xs sm:text-sm"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Searching Email...</span>
                    </>
                  ) : (
                    <span>Search Booking</span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="p-3.5 rounded-xl border border-rose-100 bg-rose-50 text-rose-800 text-[11px] sm:text-xs font-semibold flex items-start gap-2.5 max-w-md mx-auto">
            <FaInfoCircle className="text-rose-600 flex-shrink-0 text-sm mt-0.5" />
            <span>{error}</span>
          </div>
        )}


      </div>
    </dialog>
  );
};

export default ManageBookingModal;
